import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Menu } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { MENU_ITEMS } from '@/devFrontData/menu';
import { useTheme } from '@/contexts/themeContext';
import { ICON_MAPPER } from '@/constant/icons';

const { SubMenu } = Menu;

interface UpdatedMenuProps {
  isMobile?: boolean;
}

const TabsSubMenuExample: React.FC<UpdatedMenuProps> = ({
  isMobile = false,
}) => {
  const { sidebarCollapseState } = useTheme();
  const [globalRectTop, setGlobalRectTop] = useState<number>(0);
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState<string>('1');
  const [openKeys, setOpenKeys] = useState<string[]>([]); // state for open submenus
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [submenuIndicatorStyles, setSubmenuIndicatorStyles] = useState<
    Record<string, React.CSSProperties>
  >({});

  const updateMenuPosition = () => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      if (rect.top > 0) {
        setGlobalRectTop(rect.top);
      }
    }
  };

  const handleClick = (e: any) => {
    setActiveKey(e.key);

    // Helper function to find the clicked menu item by key (supports nested items)
    const findMenuItem = (items: any[]): any => {
      for (const item of items) {
        if (item.key === e.key) return item;
        if (item.children) {
          const found = findMenuItem(item.children);
          if (found) return found;
        }
      }
      return null;
    };

    const clickedItem = findMenuItem(MENU_ITEMS);
    if (clickedItem) {
      if (clickedItem.children && clickedItem.children.length > 0) {
        const firstVisibleChild = clickedItem.children.find(
          (child: any) => child.display,
        );
        if (firstVisibleChild) {
          setActiveKey(firstVisibleChild.key);
          navigate(firstVisibleChild.link);
          setOpenKeys([clickedItem.key]);
        }
      } else if (clickedItem.link) {
        navigate(clickedItem.link);
      }
    }

    if (MENU_ITEMS.find((item) => item.key === e.key && !item.children)) {
      setOpenKeys([]);
    }
  };

  // When submenu is opened or closed, only allow one submenu open at a time.
  const handleOpenChange = (keys: string[]) => {
    // If keys is empty, all submenus are closed
    if (keys.length === 0) {
      setOpenKeys([]);
      return;
    }
    // Otherwise, take the last key (the one that was just opened)
    const latestOpenKey = keys[keys.length - 1];
    setOpenKeys([latestOpenKey]);
  };

  const updateSubmenuIndicators = () => {
    if (!menuRef.current || globalRectTop === 0) return;

    requestAnimationFrame(() => {
      const newStyles: { [key: string]: React.CSSProperties } = {};

      MENU_ITEMS.forEach((menu) => {
        if (menu.children && openKeys.includes(menu.key)) {
          const submenuElement = menuRef.current!.querySelector(
            `li.ant-menu-submenu[data-submenu-key="${menu.key}"]`,
          );

          if (submenuElement) {
            const activeChild = submenuElement.querySelector(
              '.ant-menu-item-selected',
            );

            if (activeChild) {
              const childRect = activeChild.getBoundingClientRect();
              newStyles[menu.key] = {
                position: 'absolute',
                left: 30,
                top: childRect.top - globalRectTop + 4,
                width: 2,
                height: childRect.height - 10,
                backgroundColor: '#F5BF36',
                transition: 'top 0.3s ease, height 0.3s ease',
                zIndex: 1,
              };
            }
          }
        }
      });

      setSubmenuIndicatorStyles(newStyles);
    });
  };

  // When active/open/collapse changes
  useEffect(() => {
    updateMenuPosition();
    const timeout = setTimeout(() => {
      updateMenuPosition();
      updateSubmenuIndicators();
    }, 100); // Delay to wait for Drawer/Sidebar to open animation

    return () => clearTimeout(timeout);
  }, [activeKey, openKeys, sidebarCollapseState]);
  useLayoutEffect(() => {
    const findActiveMenuItem = (
      items: any[],
    ): { key: string; parentKey?: string } | null => {
      for (const item of items) {
        if (item.link && location.pathname.startsWith(item.link)) {
          return { key: item.key, parentKey: undefined };
        }
        if (item.children) {
          const found = findActiveMenuItem(item.children);
          if (found) return { key: found.key, parentKey: item.key };
        }
      }
      return null;
    };

    const activeItem = findActiveMenuItem(MENU_ITEMS);
    if (activeItem) {
      setActiveKey(activeItem.key);
      setOpenKeys(activeItem.parentKey ? [activeItem.parentKey] : []);
    }
  }, [location.pathname]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={menuRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Menu
          mode="inline"
          style={{ background: 'transparent' }}
          selectedKeys={[activeKey]}
          onClick={handleClick}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          inlineCollapsed={sidebarCollapseState && !isMobile}
        >
          {MENU_ITEMS.map((menu) =>
            menu.children ? (
              <SubMenu
                style={{
                  overflow: 'hidden',
                  display: menu.display ? 'block' : 'none',
                }}
                icon={menu.icon ? ICON_MAPPER[menu.icon] : <MailOutlined />}
                title={menu.label}
                key={menu.key}
                data-submenu-key={menu.key}
                className="submenu-with-border"
                onTitleClick={() => {
                  setActiveKey(menu.key);
                  if (menu.link) {
                    navigate(menu.link);
                  }
                }}
              >
                {menu.children.map((child) => (
                  <Menu.Item
                    key={child.key ?? `${menu.key}-child-${child.label}`}
                    style={{
                      display: child.display ? 'block' : 'none',
                    }}
                    icon={
                      child.icon ? ICON_MAPPER[child.icon] : <MailOutlined />
                    }
                  >
                    {child.label}
                  </Menu.Item>
                ))}
              </SubMenu>
            ) : (
              <Menu.Item
                key={menu.key}
                icon={menu.icon ? ICON_MAPPER[menu.icon] : <MailOutlined />}
              >
                {menu.label}
              </Menu.Item>
            ),
          )}
        </Menu>
      </div>
      {/* Render indicator styles outside SubMenu */}
      {Object.entries(submenuIndicatorStyles).map(([key, style]) => (
        <div key={key} style={style} />
      ))}

      {/* Moved outside to avoid DOM contamination */}
      <div className="sub-menu-border"></div>
    </div>
  );
};

export default TabsSubMenuExample;
